'use client'

import CastingCard from '@/components/_cards/casters/casting-card'
import { useCallback, useEffect, useRef, useState } from 'react'

const PAGE_SIZE = 24

export default function CastingResultsGrid({
  initialProfiles,
  searchParams,
  totalCount,
}) {
  const [profiles, setProfiles] = useState(initialProfiles)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const loaderRef = useRef(null)

  useEffect(() => {
    setProfiles(initialProfiles)
    const distinctUsers = new Set(initialProfiles?.map((p) => p.userId) || [])
    setHasMore(distinctUsers.size < totalCount)
  }, [initialProfiles, totalCount])

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return

    setIsLoadingMore(true)
    try {
      // Calculate skip based on distinct users properly fetched so far
      const currentSkip = new Set(profiles.map((p) => p.userId)).size

      const params = new URLSearchParams()

      for (const [key, value] of Object.entries(searchParams || {})) {
        if (value === undefined || value === null || value === '') {
          continue
        }

        if (Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, String(value))
        }
      }

      params.set('skip', String(currentSkip))
      params.set('pageSize', String(PAGE_SIZE))

      const response = await fetch(
        `/api/internal/search/casting?${params.toString()}`,
      )

      if (!response.ok) {
        throw new Error('Failed to load more casting profiles')
      }

      const payload = await response.json()
      const newProfiles = payload.profiles || []

      if (newProfiles.length > 0) {
        setProfiles((prev) => [...prev, ...newProfiles])
        setHasMore(
          currentSkip + new Set(newProfiles.map((p) => p.userId)).size <
            totalCount,
        )
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error loading more profiles:', error)
      setHasMore(false)
    } finally {
      setIsLoadingMore(false)
    }
  }, [profiles, hasMore, isLoadingMore, searchParams])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { threshold: 0.1 },
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => observer.disconnect()
  }, [loadMore])

  return (
    <div className="screen-grid col-span-full mt-6 desktop-sm:mt-8">
      {!profiles || profiles.length < 1 ? (
        <div className="col-span-full desktop-sm:col-span-10 desktop-sm:col-start-2">
          <p className="w-full pb-[70px] pt-10 text-center text-shades-0">
            Sorry, there are currently no profiles that match the search
            criteria...
          </p>
        </div>
      ) : (
        <div className="col-span-full grid gap-5 tablet:grid-cols-2 desktop-sm:col-span-10 desktop-sm:col-start-2 desktop-md:grid-cols-3 desktop-lg:gap-9">
          {profiles.map((profile) => (
            <CastingCard
              key={`${profile.userId}-${profile.role}`}
              profile={profile}
            />
          ))}

          {/* Loader Sentinel */}
          {hasMore && (
            <div
              ref={loaderRef}
              className="col-span-full flex h-20 items-center justify-center"
            >
              {isLoadingMore && (
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-blue-600 border-t-primary-500" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
